import { useCallback, useEffect, useRef, useState } from "react";
import { fetchProspectThread, type ThreadMessage } from "@/services/pipeline";
import { sendPartnerText } from "@/services/waPartner";
import { setConversationStatus, windowOpen } from "@/services/waAgent";

const WHO_LABEL: Record<ThreadMessage["who"], string> = { contact: "", bot: "Agente IA", human: "Tú", template: "Invitación" };

export default function ContactThread({ prospectId, isWhatsApp, notify }: { prospectId: string; isWhatsApp: boolean; notify: (m: string) => void }) {
  const [msgs, setMsgs] = useState<ThreadMessage[]>([]);
  const [convId, setConvId] = useState<string | null>(null);
  const [lastIn, setLastIn] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const t = await fetchProspectThread(prospectId, isWhatsApp);
      setMsgs(t.messages);
      setConvId(t.conversationId);
      setLastIn(t.lastInboundAt);
      setStatus(t.status);
    } catch {
      /* se reintenta */
    } finally {
      setLoading(false);
    }
  }, [prospectId, isWhatsApp]);

  useEffect(() => {
    setLoading(true);
    void load();
    const t = window.setInterval(() => void load(), 10000);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [msgs.length]);

  const open = windowOpen(lastIn);

  const toggle = async (to: "bot" | "human") => {
    if (!convId) return;
    try {
      await setConversationStatus(convId, to);
      setStatus(to);
      notify(to === "bot" ? "El agente IA atiende esta conversación" : "Ahora la atiendes tú");
    } catch {
      notify("No se pudo cambiar");
    }
  };

  const send = async () => {
    if (!convId || !text.trim() || busy) return;
    setBusy(true);
    const r = await sendPartnerText(convId, text.trim());
    setBusy(false);
    if (r.ok) {
      setText("");
      setStatus("human");
      await load();
    } else notify(r.error === "window_closed" ? "Pasaron más de 24 h: espera a que te escriba o invítalo de nuevo" : r.error ?? "No se pudo enviar");
  };

  if (loading) return <div className="text-sm text-foreground-500">Cargando conversación…</div>;
  if (!msgs.length) return <div className="text-sm text-foreground-500">Todavía no hay mensajes en este hilo.</div>;

  return (
    <div>
      {isWhatsApp && convId && status !== "closed" ? (
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${status === "bot" ? "bg-primary-500/15 text-primary-700" : "bg-amber-500/15 text-amber-700"}`}>
            {status === "bot" ? "Responde el agente IA" : "Lo atiendes tú"}
          </span>
          <button className="text-xs font-semibold text-primary-700 cursor-pointer" onClick={() => void toggle(status === "bot" ? "human" : "bot")}>
            {status === "bot" ? "Tomar yo la conversación" : "Devolver al agente"}
          </button>
        </div>
      ) : null}
      <div className="max-h-72 overflow-y-auto rounded-xl bg-background-50 border border-background-200 p-3 space-y-2">
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.mine ? (m.who === "bot" ? "bg-primary-500/20 text-foreground-950" : "bg-primary-500 text-background-950") : "bg-background-100 border border-background-200 text-foreground-900"}`}>
              {m.body}
              <div className="text-[10px] opacity-70 mt-1 text-right">
                {m.mine && WHO_LABEL[m.who] ? `${WHO_LABEL[m.who]} · ` : ""}
                {new Date(m.createdAt).toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          </div>
        ))}
        <div ref={end}></div>
      </div>
      {isWhatsApp && convId ? (
        open ? (
          <div className="flex gap-2 mt-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void send()}
              placeholder="Escribe tu respuesta…"
              className="flex-1 bg-background-100 border border-background-200 rounded-full px-4 py-2 text-sm outline-none focus:border-primary-400"
            />
            <button disabled={busy || !text.trim()} onClick={() => void send()} className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 disabled:opacity-40 cursor-pointer">
              Enviar
            </button>
          </div>
        ) : (
          <p className="text-[11px] text-foreground-500 mt-2">Pasaron más de 24 h desde su último mensaje: WhatsApp solo permite escribirle de nuevo cuando él te escriba.</p>
        )
      ) : !isWhatsApp ? (
        <p className="text-[11px] text-foreground-500 mt-2">Esta conversación es por mensaje directo: respóndela desde Prospectar.</p>
      ) : null}
    </div>
  );
}
